<template>
  <bubble-menu
    v-if="editor"
    :editor="editor"
    :tippy-options="tippyOptions"
    :should-show="shouldShow"
    class="table-bubble-menu"
  >
    <div class="bubble-menu-content">
      <!-- Row Actions Dropdown -->
      <BubbleMenuDropdown :items="rowItems">
        <template #trigger>
          <RowIcon :width="20" :height="20" />
        </template>
      </BubbleMenuDropdown>

      <div class="bbl-menu-divider"></div>

      <!-- Column Actions Dropdown -->
      <BubbleMenuDropdown :items="columnItems">
        <template #trigger>
          <ColumnIcon :width="20" :height="20" />
        </template>
      </BubbleMenuDropdown>

      <div class="bbl-menu-divider"></div>

      <!-- Cell Actions Dropdown -->
      <BubbleMenuDropdown :items="cellItems">
        <template #trigger>
          <CellIcon :width="18" :height="18" />
        </template>
      </BubbleMenuDropdown>

      <div class="bbl-menu-divider"></div>

      <!-- Alignment Dropdown -->
      <BubbleMenuDropdown :items="alignmentItems">
        <template #trigger>
          <AlignIcon :width="18" :height="18" />
        </template>
      </BubbleMenuDropdown>

      <div class="bbl-menu-divider"></div>

      <!-- Color Picker -->
      <div class="color-picker-wrapper" ref="colorPicker">
        <button
          class="menu-btn"
          @click="toggleColorPicker"
          title="Cell Background Color"
        >
          <ColorPalette />
        </button>
        <div v-if="showColorPicker" class="color-picker-dropdown">
          <div class="color-section">
            <button
              class="color-action-button"
              @click="setCellBackground('')"
              type="button"
            >
              <span class="remove-icon">✕</span> Clear Background
            </button>
          </div>
          
          <div class="color-section">
            <div class="section-label">Background Colors</div>
            <div class="color-grid">
              <button
                v-for="color in colorOptions"
                :key="color.value"
                @click="setCellBackground(color.value)"
                class="color-option"
                :style="{ backgroundColor: color.value }"
                :title="color.name"
                type="button"
              />
            </div>
          </div>

          <div class="color-section">
            <div class="section-label">Custom Color</div>
            <div class="custom-color-picker">
              <input
                type="color"
                v-model="customColor"
                @change="applyCustomColor"
                class="color-input"
              />
              <input
                type="text"
                v-model="customColor"
                @keyup.enter="applyCustomColor"
                @blur="validateCustomColor"
                placeholder="#ffffff"
                class="color-text-input"
                maxlength="7"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="bbl-menu-divider"></div>

      <!-- Delete Table -->
      <button
        class="menu-btn delete-btn"
        @click="deleteTable"
        title="Delete Table"
      >
        <DeleteTable />
      </button>
    </div>
  </bubble-menu>
</template>

<script>
import { BubbleMenu } from '@tiptap/vue-2'
import BubbleMenuDropdown from '../Toolbar/BubbleMenuDropdown.vue'
import {
  DeleteTable,
  LeftAlign,
  CenterAlign,
  RightAlign,
  JustifyAlign,
  ColorPalette,
  AboveRow,
  BelowRow,
  MoveUp,
  MoveDown,
  Duplicate,
  AboveColumn,
  BelowColumn,
  MoveLeft,
  MoveRight,
  RowIcon,
  ColumnIcon,
  CellIcon,
  AlignIcon,
  MergeCells,
  SplitCell
} from '../svg'

export default {
  name: 'TableBubbleMenu',
  components: {
    BubbleMenu,
    BubbleMenuDropdown,
    DeleteTable,
    ColorPalette,
    RowIcon,
    ColumnIcon,
    CellIcon,
    AlignIcon
  },
  props: {
    editor: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      showColorPicker: false,
      customColor: '#ffffff',
      tippyOptions: {
        duration: 100,
        placement: 'top',
        maxWidth: 'none',
        getReferenceClientRect: () => {
          if (!this.editor) return null
          
          const { state } = this.editor
          const { selection } = state
          const { $anchor } = selection
          
          // Find the table node
          let tableDepth = null
          for (let d = $anchor.depth; d > 0; d--) {
            if ($anchor.node(d).type.name === 'table') {
              tableDepth = d
              break
            }
          }
          
          if (tableDepth !== null) {
            const tablePos = $anchor.before(tableDepth)
            const tableNode = state.doc.nodeAt(tablePos)
            
            if (tableNode) {
              // Get the DOM element for the table
              const tableDOM = this.editor.view.nodeDOM(tablePos)
              
              if (tableDOM && tableDOM.getBoundingClientRect) {
                const rect = tableDOM.getBoundingClientRect()
                // Return the top of the table
                return {
                  top: rect.top,
                  left: rect.left,
                  right: rect.right,
                  bottom: rect.top, // Use top for bottom to position menu at table top
                  width: rect.width,
                  height: 0
                }
              }
            }
          }
          
          // Fallback to selection rect
          return this.editor.view.coordsAtPos(selection.$anchor.pos)
        }
      },
      colorOptions: [
        // Row 1 - Pastels
        { name: 'Light Gray', value: '#f3f4f6' },
        { name: 'Light Blue', value: '#dbeafe' },
        { name: 'Light Green', value: '#dcfce7' },
        { name: 'Light Yellow', value: '#fef3c7' },
        
        // Row 2 - More Pastels
        { name: 'Light Red', value: '#fee2e2' },
        { name: 'Light Purple', value: '#f3e8ff' },
        { name: 'Light Pink', value: '#fce7f3' },
        { name: 'Light Orange', value: '#ffedd5' },
        
        // Row 3 - Medium Tones
        { name: 'Gray', value: '#e5e7eb' },
        { name: 'Sky Blue', value: '#bfdbfe' },
        { name: 'Mint', value: '#bbf7d0' },
        { name: 'Cream', value: '#fef08a' },
        
        // Row 4 - Medium Tones
        { name: 'Coral', value: '#fecaca' },
        { name: 'Lavender', value: '#e9d5ff' },
        { name: 'Rose', value: '#fbcfe8' },
        { name: 'Peach', value: '#fed7aa' },
        
        // Row 5 - Deeper Tones
        { name: 'Slate', value: '#cbd5e1' },
        { name: 'Blue', value: '#93c5fd' },
        { name: 'Green', value: '#86efac' },
        { name: 'Yellow', value: '#fde047' },
        
        // Row 6 - Deeper Tones
        { name: 'Red', value: '#fca5a5' },
        { name: 'Purple', value: '#d8b4fe' },
        { name: 'Pink', value: '#f9a8d4' },
        { name: 'Orange', value: '#fdba74' }
      ]
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  computed: {
    rowItems() {
      return [
        {
          label: 'Insert Row Above',
          icon: AboveRow,
          action: () => this.editor.chain().focus().addRowBefore().run()
        },
        {
          label: 'Insert Row Below',
          icon: BelowRow,
          action: () => this.editor.chain().focus().addRowAfter().run()
        },
        {
          label: 'Move Row Up',
          icon: MoveUp,
          action: () => this.moveRowUp()
        },
        {
          label: 'Move Row Down',
          icon: MoveDown,
          action: () => this.moveRowDown()
        },
        {
          label: 'Duplicate Row',
          icon: Duplicate,
          action: () => this.duplicateRow()
        },
        {
          label: 'Delete Row',
          icon: DeleteTable,
          action: () => this.editor.chain().focus().deleteRow().run()
        }
      ]
    },
    
    columnItems() {
      return [
        {
          label: 'Insert Column Left',
          icon: AboveColumn,
          action: () => this.editor.chain().focus().addColumnBefore().run()
        },
        {
          label: 'Insert Column Right',
          icon: BelowColumn,
          action: () => this.editor.chain().focus().addColumnAfter().run()
        },
        {
          label: 'Move Column Left',
          icon: MoveLeft,
          action: () => this.moveColumnLeft()
        },
        {
          label: 'Move Column Right',
          icon: MoveRight,
          action: () => this.moveColumnRight()
        },
        {
          label: 'Duplicate Column',
          icon: Duplicate,
          action: () => this.duplicateColumn()
        },
        {
          label: 'Delete Column',
          icon: DeleteTable,
          action: () => this.editor.chain().focus().deleteColumn().run()
        }
      ]
    },
    
    cellItems() {
      return [
        {
          label: 'Merge Cells',
          icon: MergeCells,
          action: () => this.editor.chain().focus().mergeCells().run(),
          disabled: !this.editor.can().mergeCells()
        },
        {
          label: 'Split Cell',
          icon: SplitCell,
          action: () => this.editor.chain().focus().splitCell().run(),
          disabled: !this.editor.can().splitCell()
        },
      ]
    },

    alignmentItems() {
      return [
        {
          label: 'Align Left',
          icon: LeftAlign,
          action: () => this.setCellAlignment('left')
        },
        {
          label: 'Align Center',
          icon: CenterAlign,
          action: () => this.setCellAlignment('center')
        },
        {
          label: 'Align Right',
          icon: RightAlign,
          action: () => this.setCellAlignment('right')
        },
        {
          label: 'Justify',
          icon: JustifyAlign,
          action: () => this.setCellAlignment('justify')
        }
      ]
    }
  },
  methods: {
    shouldShow({ editor }) {
      // Show when cursor is in a table
      return editor.isActive('table')
    },
    
    deleteTable() {
      this.editor.chain().focus().deleteTable().run()
    },

    toggleColorPicker() {
      this.showColorPicker = !this.showColorPicker
    },

    handleClickOutside(e) {
      if (this.$refs.colorPicker && !this.$refs.colorPicker.contains(e.target)) {
        this.showColorPicker = false
      }
    },

    applyCustomColor() {
      if (this.isValidColor(this.customColor)) {
        this.setCellBackground(this.customColor)
      }
    },

    validateCustomColor() {
      if (!this.isValidColor(this.customColor)) {
        this.customColor = '#ffffff'
      }
    },

    isValidColor(color) {
      // Check if it's a valid hex color
      return /^#[0-9A-F]{6}$/i.test(color)
    },

    setCellAlignment(alignment) {
      if (!this.editor) return
      
      const { state, view } = this.editor
      const { selection } = state
      const { $anchor } = selection
      
      let cellDepth = null
      for (let d = $anchor.depth; d > 0; d--) {
        const node = $anchor.node(d)
        if (node.type.name === 'tableCell' || node.type.name === 'tableHeader') {
          cellDepth = d
          break
        }
      }
      
      if (cellDepth !== null) {
        const cellPos = $anchor.before(cellDepth)
        const cell = state.doc.nodeAt(cellPos)
        
        if (cell) {
          const tr = state.tr
          const existingAttrs = cell.attrs || {}
          const existingStyle = existingAttrs.style || ''
          
          const cleanedStyle = existingStyle
            .split(';')
            .filter(s => s.trim() && !s.trim().startsWith('text-align'))
            .join(';')
          
          const newStyle = cleanedStyle 
            ? `${cleanedStyle}; text-align: ${alignment}`
            : `text-align: ${alignment}`
          
          tr.setNodeMarkup(cellPos, null, {
            ...existingAttrs,
            style: newStyle
          })
          
          view.dispatch(tr)
          this.editor.commands.focus()
        }
      }
    },

    setCellBackground(color) {
      if (!this.editor) return
      
      const { state, view } = this.editor
      const { selection } = state
      const { $anchor } = selection
      
      let cellDepth = null
      for (let d = $anchor.depth; d > 0; d--) {
        const node = $anchor.node(d)
        if (node.type.name === 'tableCell' || node.type.name === 'tableHeader') {
          cellDepth = d
          break
        }
      }
      
      if (cellDepth !== null) {
        const cellPos = $anchor.before(cellDepth)
        const cell = state.doc.nodeAt(cellPos)
        
        if (cell) {
          const tr = state.tr
          const existingAttrs = cell.attrs || {}
          const existingStyle = existingAttrs.style || ''
          
          const cleanedStyle = existingStyle
            .split(';')
            .filter(s => s.trim() && !s.trim().startsWith('background-color'))
            .join(';')
          
          const newStyle = color
            ? (cleanedStyle ? `${cleanedStyle}; background-color: ${color}` : `background-color: ${color}`)
            : cleanedStyle
          
          tr.setNodeMarkup(cellPos, null, {
            ...existingAttrs,
            style: newStyle || undefined
          })
          
          view.dispatch(tr)
          this.editor.commands.focus()
        }
      }
      
      this.showColorPicker = false
    },

    duplicateRow() {
      if (!this.editor) return
      
      const { state } = this.editor
      const { selection } = state
      const { $anchor } = selection
      
      let rowDepth = null
      for (let d = $anchor.depth; d > 0; d--) {
        if ($anchor.node(d).type.name === 'tableRow') {
          rowDepth = d
          break
        }
      }
      
      if (rowDepth !== null) {
        const rowPos = $anchor.before(rowDepth)
        const row = state.doc.nodeAt(rowPos)
        
        if (row) {
          const tr = state.tr
          const duplicatedRow = row.copy(row.content)
          tr.insert(rowPos + row.nodeSize, duplicatedRow)
          this.editor.view.dispatch(tr)
          this.editor.commands.focus()
        }
      }
    },

    duplicateColumn() {
      if (!this.editor) return
      
      const { state } = this.editor
      const { selection } = state
      const { $anchor } = selection
      
      let cellDepth = null
      for (let d = $anchor.depth; d > 0; d--) {
        const node = $anchor.node(d)
        if (node.type.name === 'tableCell' || node.type.name === 'tableHeader') {
          cellDepth = d
          break
        }
      }
      
      if (cellDepth !== null) {
        const cellPos = selection.$anchor.before(cellDepth)
        const $cell = state.doc.resolve(cellPos)
        
        let colIndex = 0
        let currentPos = $cell.before($cell.depth)
        const row = $cell.node($cell.depth - 1)
        
        row.forEach((cell) => {
          if (currentPos === cellPos) {
            return false
          }
          colIndex++
          currentPos += cell.nodeSize
        })
        
        const tableDepth = $cell.depth - 2
        const table = $cell.node(tableDepth)
        const columnData = []
        
        table.forEach((row) => {
          let cellIndex = 0
          row.forEach((cell) => {
            if (cellIndex === colIndex) {
              columnData.push({
                content: cell.content,
                attrs: cell.attrs,
                type: cell.type.name
              })
            }
            cellIndex++
          })
        })
        
        this.editor.chain().focus().addColumnAfter().run()
        
        setTimeout(() => {
          const newState = this.editor.state
          const newSelection = newState.selection
          const new$anchor = newSelection.$anchor
          
          let newCellDepth = null
          for (let d = new$anchor.depth; d > 0; d--) {
            const node = new$anchor.node(d)
            if (node.type.name === 'tableCell' || node.type.name === 'tableHeader') {
              newCellDepth = d
              break
            }
          }
          
          if (newCellDepth !== null) {
            const new$cell = newState.doc.resolve(newSelection.$anchor.before(newCellDepth))
            const newTableDepth = new$cell.depth - 2
            const newTable = new$cell.node(newTableDepth)
            const newTablePos = new$cell.before(newTableDepth)
            
            const tr = newState.tr
            let rowIndex = 0
            
            newTable.forEach((row, offset) => {
              if (rowIndex < columnData.length) {
                let cellIndex = 0
                const targetColIndex = colIndex + 1
                
                row.forEach((cell, cellOffset) => {
                  if (cellIndex === targetColIndex) {
                    const cellPos = newTablePos + offset + cellOffset + 1
                    const sourceData = columnData[rowIndex]
                    
                    tr.replaceWith(
                      cellPos,
                      cellPos + cell.nodeSize,
                      newState.schema.nodes[sourceData.type].create(
                        sourceData.attrs,
                        sourceData.content
                      )
                    )
                  }
                  cellIndex++
                })
              }
              rowIndex++
            })
            
            this.editor.view.dispatch(tr)
            this.editor.commands.focus()
          }
        }, 50)
      }
    },

    moveRowUp() {
      if (!this.editor) return
      
      const { state } = this.editor
      const { selection } = state
      const { $anchor } = selection
      
      let rowDepth = null
      for (let d = $anchor.depth; d > 0; d--) {
        if ($anchor.node(d).type.name === 'tableRow') {
          rowDepth = d
          break
        }
      }
      
      if (rowDepth !== null) {
        const tableDepth = rowDepth - 1
        const table = $anchor.node(tableDepth)
        const rowPos = $anchor.before(rowDepth)
        const tablePos = $anchor.before(tableDepth)
        
        let rowIndex = 0
        let currentPos = tablePos + 1
        
        table.forEach((row) => {
          if (currentPos === rowPos) {
            return false
          }
          rowIndex++
          currentPos += row.nodeSize
        })
        
        if (rowIndex > 0) {
          const currentRow = state.doc.nodeAt(rowPos)
          
          let prevRowPos = tablePos + 1
          let prevRowIndex = 0
          table.forEach((row) => {
            if (prevRowIndex === rowIndex - 1) {
              return false
            }
            prevRowPos += row.nodeSize
            prevRowIndex++
          })
          
          const tr = state.tr
          tr.delete(rowPos, rowPos + currentRow.nodeSize)
          tr.insert(prevRowPos, currentRow.copy(currentRow.content))
          
          this.editor.view.dispatch(tr)
          this.editor.commands.focus()
        }
      }
    },

    moveRowDown() {
      if (!this.editor) return
      
      const { state } = this.editor
      const { selection } = state
      const { $anchor } = selection
      
      let rowDepth = null
      for (let d = $anchor.depth; d > 0; d--) {
        if ($anchor.node(d).type.name === 'tableRow') {
          rowDepth = d
          break
        }
      }
      
      if (rowDepth !== null) {
        const tableDepth = rowDepth - 1
        const table = $anchor.node(tableDepth)
        const rowPos = $anchor.before(rowDepth)
        const tablePos = $anchor.before(tableDepth)
        
        let rowIndex = 0
        let totalRows = 0
        let currentPos = tablePos + 1
        
        table.forEach((row) => {
          if (currentPos === rowPos) {
            rowIndex = totalRows
          }
          totalRows++
          currentPos += row.nodeSize
        })
        
        if (rowIndex < totalRows - 1) {
          const currentRow = state.doc.nodeAt(rowPos)
          const nextRowPos = rowPos + currentRow.nodeSize
          const nextRow = state.doc.nodeAt(nextRowPos)
          
          const tr = state.tr
          tr.delete(rowPos, rowPos + currentRow.nodeSize)
          tr.insert(nextRowPos + nextRow.nodeSize - currentRow.nodeSize, currentRow.copy(currentRow.content))
          
          this.editor.view.dispatch(tr)
          this.editor.commands.focus()
        }
      }
    },

    moveColumnLeft() {
      if (!this.editor) return
      
      const { state } = this.editor
      const { selection } = state
      
      let cellDepth = null
      for (let d = selection.$anchor.depth; d > 0; d--) {
        const node = selection.$anchor.node(d)
        if (node.type.name === 'tableCell' || node.type.name === 'tableHeader') {
          cellDepth = d
          break
        }
      }
      
      if (cellDepth !== null) {
        const cellPos = selection.$anchor.before(cellDepth)
        const $cell = state.doc.resolve(cellPos)
        
        let colIndex = 0
        let currentPos = $cell.before($cell.depth)
        const row = $cell.node($cell.depth - 1)
        
        row.forEach((cell) => {
          if (currentPos === cellPos) {
            return false
          }
          colIndex++
          currentPos += cell.nodeSize
        })
        
        if (colIndex > 0) {
          this.editor.chain().focus().deleteColumn().run()
          
          setTimeout(() => {
            this.editor.chain().focus().addColumnBefore().run()
          }, 10)
        }
      }
    },

    moveColumnRight() {
      if (!this.editor) return
      
      const { state } = this.editor
      const { selection } = state
      
      let cellDepth = null
      for (let d = selection.$anchor.depth; d > 0; d--) {
        const node = selection.$anchor.node(d)
        if (node.type.name === 'tableCell' || node.type.name === 'tableHeader') {
          cellDepth = d
          break
        }
      }
      
      if (cellDepth !== null) {
        const cellPos = selection.$anchor.before(cellDepth)
        const $cell = state.doc.resolve(cellPos)
        
        let colIndex = 0
        let totalCols = 0
        let currentPos = $cell.before($cell.depth)
        const row = $cell.node($cell.depth - 1)
        
        row.forEach((cell) => {
          if (currentPos === cellPos) {
            colIndex = totalCols
          }
          totalCols++
          currentPos += cell.nodeSize
        })
        
        if (colIndex < totalCols - 1) {
          this.editor.chain().focus().deleteColumn().run()
          
          setTimeout(() => {
            this.editor.chain().focus().addColumnAfter().run()
          }, 10)
        }
      }
    }
  }
}
</script>

<style scoped>
.table-bubble-menu {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 4px;
  display: flex;
  gap: 4px;
}

.bubble-menu-content {
  display: flex;
  align-items: center;
  gap: 4px;
}

.bbl-menu-divider {
  width: 1px;
  height: 24px;
  background: #e5e7eb;
  margin: 0 4px;
}

.menu-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #374151;
  transition: all 0.15s ease;
  padding: 6px;
}

.menu-btn:hover {
  background: #f3f4f6;
}

.menu-btn svg {
  width: 18px;
  height: 18px;
}

.delete-btn {
  color: #dc2626;
}

.delete-btn:hover {
  background: #fee2e2;
}

.color-picker-wrapper {
  position: relative;
}

.color-picker-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  background: white;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 8px;
  z-index: 1000;
  min-width: 180px;
}

.color-section {
  margin-bottom: 12px;
}

.color-section:last-child {
  margin-bottom: 0;
}

.section-label {
  font-size: 12px;
  color: #666;
  margin-bottom: 6px;
  font-weight: 500;
}

.color-action-button {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.color-action-button:hover {
  background: #f5f5f5;
  border-color: #999;
}

.remove-icon {
  color: #e74c3c;
  font-weight: bold;
}

.color-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
}

.color-option {
  width: 32px;
  height: 32px;
  border: 2px solid #e5e7eb;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;
  padding: 0;
}

.color-option:hover {
  border-color: #3b82f6;
  transform: scale(1.1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 1;
}

.custom-color-picker {
  display: flex;
  gap: 6px;
  align-items: center;
}

.color-input {
  width: 40px;
  height: 32px;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  padding: 2px;
}

.color-input:hover {
  border-color: #3b82f6;
}

.color-text-input {
  flex: 1;
  padding: 6px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
  font-family: monospace;
  text-transform: uppercase;
}

.color-text-input:focus {
  outline: none;
  border-color: #3b82f6;
}

.color-text-input::placeholder {
  text-transform: none;
}
</style>
